import React from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import type { ShareLevel } from "src/api/shares";

// "manage" is the admin tier for the resources this dialog shares (MRE
// #30597): a rank-3 grant is full control, not ownership — a dataset's owner
// is its create_user_fk, and transferring ownership hands the outgoing owner a
// manage grant. The project-folder domain still says "owner" for the same
// tier, and the backend accepts either on input, so a grant that arrives
// labelled "owner" is shown and re-sent as Manage. Converging the folder
// vocabulary is MRE #30687.
export const normalizeShareLevel = (level: ShareLevel): ShareLevel =>
  level === "owner" ? "manage" : level;

const permissionOptions: {
  value: ShareLevel;
  label: string;
  description: string;
}[] = [
  { value: "read", label: "View only", description: "Can view the data" },
  { value: "write", label: "Can edit", description: "Can add and edit data" },
  {
    value: "manage",
    label: "Manage",
    description: "Full control including sharing",
  },
];

export const labelForLevel = (level: ShareLevel) => {
  const normalized = normalizeShareLevel(level);
  // Fall back to the normalized value, not the raw one, so a future edit to
  // permissionOptions cannot leak "owner" back into the UI.
  return (
    permissionOptions.find((o) => o.value === normalized)?.label ?? normalized
  );
};

interface PermissionSelectProps {
  value: ShareLevel;
  onChange: (level: ShareLevel) => void;
  testId: string;
  disabled?: boolean;
}

const PermissionSelect: React.FC<PermissionSelectProps> = ({
  value,
  onChange,
  testId,
  disabled = false,
}) => (
  <FormControl size="small" sx={{ minWidth: 140 }}>
    <InputLabel>Permission</InputLabel>
    <Select
      value={value}
      label="Permission"
      data-testid={testId}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as ShareLevel)}
      renderValue={(val) => labelForLevel(val as ShareLevel)}
    >
      {permissionOptions.map((opt) => (
        <MenuItem
          key={opt.value}
          value={opt.value}
          data-testid={`${testId}-option-${opt.value}`}
        >
          <Stack>
            <Typography variant="body2">{opt.label}</Typography>
            <Typography variant="caption" color="text.secondary">
              {opt.description}
            </Typography>
          </Stack>
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

export default PermissionSelect;
